import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  FileText,
  ArrowLeft,
  Search,
  Download,
  Eye,
  Folder,
  File,
  FileSpreadsheet,
  FileImage,
  ChevronRight,
  Sparkles,
  Loader2,
  Upload,
  Plus,
  Share2,
  Users,
  UserCheck,
} from 'lucide-react';

const docCategories = [
  { id: 'all', label: 'All Documents', icon: Folder },
  { id: 'agm_minutes', label: 'AGM Minutes', icon: FileText },
  { id: 'budget', label: 'Budgets', icon: FileSpreadsheet },
  { id: 'financial_report', label: 'Financial Reports', icon: FileSpreadsheet },
  { id: 'rules', label: 'Rules & Policies', icon: File },
  { id: 'insurance', label: 'Insurance', icon: FileImage },
  { id: 'contract', label: 'Contracts', icon: FileText },
  { id: 'other', label: 'Other', icon: File },
];

export default function DocumentVault() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hoaId: routeHoaId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    doc_type: 'rules',
    tags: '',
    share_with: ['trustees', 'residents'], // Default share with both
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharingDoc, setSharingDoc] = useState(null);
  const [shareSettings, setShareSettings] = useState({
    trustees: true,
    residents: true,
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  // Use route param first (for agent/platform routes), then ?building= query, then user's hoa_id (which AuthContext already populates from activeBuilding when applicable).
  const hoaId = routeHoaId || searchParams.get('building') || user?.hoa_id || null;
  
  // Check if user can upload (trustees, agents, managers)
  const canUpload = ['trustee', 'property_manager', 'managing_agent', 'agent_admin', 'platform_owner'].includes(user?.role);
  
  // Determine back navigation based on user role
  const getBackPath = () => {
    if (user?.role === 'trustee' || user?.role === 'property_manager') return '/admin';
    if (['managing_agent', 'agent_admin'].includes(user?.role)) return '/agent';
    if (user?.role === 'platform_owner') return '/platform';
    return '/dashboard';
  };

  useEffect(() => {
    if (hoaId) {
      fetchDocuments();
    } else {
      setLoading(false);
    }
  }, [activeCategory, hoaId]);

  const fetchDocuments = async () => {
    if (!hoaId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const docType = activeCategory === 'all' ? null : activeCategory;
      const data = await documentsApi.list(hoaId, docType);
      setDocuments(Array.isArray(data) ? data : []);
      setSearchResults(null);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setDocuments([]);
      toast.error(error?.message || 'Could not load documents — please refresh');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill title from filename if empty
      if (!uploadData.title) {
        setUploadData({...uploadData, title: file.name.replace(/\.[^/.]+$/, '')});
      }
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    if (!uploadData.title) {
      toast.error('Please enter a document title');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadData.title);
      formData.append('doc_type', uploadData.doc_type);
      if (uploadData.tags) {
        formData.append('tags', uploadData.tags);
      }

      const response = await fetch(`${API_URL}/api/documents/${hoaId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('session_token')}` },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setShowUploadDialog(false);
        setSelectedFile(null);
        setUploadData({ title: '', doc_type: 'rules', tags: '' });
        fetchDocuments();
      } else {
        const errPayload = await response.json().catch(() => ({}));
        toast.error(errPayload.detail || `Upload failed (HTTP ${response.status})`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed — please check your connection and try again');
    } finally {
      setUploading(false);
    }
  };

  const handleAISearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const results = await documentsApi.search(hoaId, searchQuery);
      setSearchResults(results.results);
      toast.success(`Found ${results.results.length} relevant documents`);
    } catch (error) {
      // Fallback to simple search
      const filtered = documents.filter(doc => 
        doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setSearchResults(filtered);
    } finally {
      setSearching(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDocIcon = (docType) => {
    const icons = {
      agm_minutes: FileText,
      budget: FileSpreadsheet,
      financial_report: FileSpreadsheet,
      rules: File,
      insurance: FileImage,
      contract: FileText,
    };
    return icons[docType] || File;
  };

  const displayDocs = searchResults || documents;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Document Vault</h1>
          </div>
          {canUpload && (
            <Button 
              size="sm" 
              onClick={() => setShowUploadDialog(true)}
              data-testid="upload-document-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Upload
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {!hoaId ? (
          <Card data-testid="no-building-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">No building linked</h3>
              <p className="text-slate-500 text-sm mb-4">
                Your account isn't connected to a building yet. Join a building to view documents.
              </p>
              <Link to="/find-building">
                <Button className="bg-sky-500 hover:bg-sky-600" data-testid="join-building-btn">Find a building</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
        <>
        {/* AI Search */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 border-0" data-testid="ai-search-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-sky-400" />
              <span className="text-sm text-white font-medium">AI-Powered Search</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAISearch()}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                data-testid="search-input"
              />
              <Button 
                onClick={handleAISearch}
                disabled={searching}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="search-btn"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
          {docCategories.map((cat) => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full whitespace-nowrap ${activeCategory === cat.id ? 'bg-slate-900' : ''}`}
              onClick={() => { setActiveCategory(cat.id); setSearchResults(null); }}
              data-testid={`category-${cat.id}`}
            >
              <cat.icon className="w-4 h-4 mr-1" />
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Search Results Label */}
        {searchResults && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {searchResults.length} results for &ldquo;{searchQuery}&rdquo;
            </p>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { setSearchResults(null); setSearchQuery(''); }}
            >
              Clear
            </Button>
          </div>
        )}

        {/* Documents List */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-20 rounded-xl" />
              <Skeleton className="h-20 rounded-xl" />
              <Skeleton className="h-20 rounded-xl" />
            </>
          ) : displayDocs.length > 0 ? (
            displayDocs.map((doc, index) => {
              const DocIcon = getDocIcon(doc.doc_type);
              return (
                <Card 
                  key={doc.document_id}
                  className="overflow-hidden animate-slideUp cursor-pointer hover:shadow-md transition-shadow"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`document-${doc.document_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                        <DocIcon className="w-6 h-6 text-sky-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{doc.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">{doc.file_name}</span>
                          <span className="text-xs text-slate-300">•</span>
                          <span className="text-xs text-slate-400">{formatFileSize(doc.file_size)}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{formatDate(doc.created_at)}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => {
                            setSharingDoc(doc);
                            setShareSettings({
                              trustees: doc.share_with?.includes('trustees') ?? true,
                              residents: doc.share_with?.includes('residents') ?? true,
                            });
                            setShowShareDialog(true);
                          }}
                          title="Share settings"
                        >
                          <Share2 className="w-4 h-4 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="w-4 h-4 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Download className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>
                    </div>
                    {doc.tags?.length > 0 && (
                      <div className="flex gap-1 mt-3 flex-wrap">
                        {doc.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs bg-slate-100">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Folder className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">No documents found</h3>
                <p className="text-slate-500 text-sm">
                  {activeCategory !== 'all' 
                    ? `No ${docCategories.find(c => c.id === activeCategory)?.label.toLowerCase()} available`
                    : 'No documents have been uploaded yet'}
                </p>
                {canUpload && (
                  <Button 
                    className="mt-4" 
                    onClick={() => setShowUploadDialog(true)}
                    data-testid="upload-first-document-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload First Document
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        </>
        )}
      </main>

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File Input */}
            <div>
              <Label>Select File</Label>
              <div 
                className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-8 h-8 text-indigo-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{selectedFile.name}</p>
                      <p className="text-sm text-slate-500">{formatFileSize(selectedFile.size)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Click to select a file</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, Word, Excel, or Image</p>
                  </>
                )}
              </div>
            </div>

            {/* Document Title */}
            <div>
              <Label>Document Title *</Label>
              <Input
                value={uploadData.title}
                onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                placeholder="Enter document title"
                className="mt-1"
              />
            </div>

            {/* Document Type */}
            <div>
              <Label>Document Type</Label>
              <Select
                value={uploadData.doc_type}
                onValueChange={(v) => setUploadData({...uploadData, doc_type: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agm_minutes">AGM Minutes</SelectItem>
                  <SelectItem value="budget">Budget</SelectItem>
                  <SelectItem value="financial_report">Financial Report</SelectItem>
                  <SelectItem value="rules">Rules & Policies</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={uploadData.tags}
                onChange={(e) => setUploadData({...uploadData, tags: e.target.value})}
                placeholder="e.g., 2026, financial, important"
                className="mt-1"
              />
            </div>

            {/* Share With */}
            <div>
              <Label className="flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                Share With
              </Label>
              <div className="mt-2 space-y-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="share-trustees"
                    checked={uploadData.share_with?.includes('trustees')}
                    onCheckedChange={(checked) => {
                      const current = uploadData.share_with || [];
                      if (checked) {
                        setUploadData({...uploadData, share_with: [...current, 'trustees']});
                      } else {
                        setUploadData({...uploadData, share_with: current.filter(s => s !== 'trustees')});
                      }
                    }}
                  />
                  <label htmlFor="share-trustees" className="text-sm flex items-center gap-2 cursor-pointer">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    Trustee Hub (Trustees & Agents)
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="share-residents"
                    checked={uploadData.share_with?.includes('residents')}
                    onCheckedChange={(checked) => {
                      const current = uploadData.share_with || [];
                      if (checked) {
                        setUploadData({...uploadData, share_with: [...current, 'residents']});
                      } else {
                        setUploadData({...uploadData, share_with: current.filter(s => s !== 'residents')});
                      }
                    }}
                  />
                  <label htmlFor="share-residents" className="text-sm flex items-center gap-2 cursor-pointer">
                    <Users className="w-4 h-4 text-green-600" />
                    Residents (Resident App)
                  </label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
            <Button onClick={handleUploadDocument} disabled={uploading || !selectedFile}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Settings Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">{sharingDoc?.title}</p>
              <p className="text-sm text-slate-500">{sharingDoc?.file_name}</p>
            </div>

            <div>
              <Label>Who can view this document?</Label>
              <div className="mt-2 space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="share-doc-trustees"
                    checked={shareSettings.trustees}
                    onCheckedChange={(checked) => setShareSettings({...shareSettings, trustees: checked})}
                  />
                  <label htmlFor="share-doc-trustees" className="text-sm flex items-center gap-2 cursor-pointer">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    <div>
                      <p className="font-medium">Trustee Hub</p>
                      <p className="text-xs text-slate-500">Trustees, Managing Agents, Staff</p>
                    </div>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="share-doc-residents"
                    checked={shareSettings.residents}
                    onCheckedChange={(checked) => setShareSettings({...shareSettings, residents: checked})}
                  />
                  <label htmlFor="share-doc-residents" className="text-sm flex items-center gap-2 cursor-pointer">
                    <Users className="w-4 h-4 text-green-600" />
                    <div>
                      <p className="font-medium">Residents</p>
                      <p className="text-xs text-slate-500">All building residents via Resident App</p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>Cancel</Button>
            <Button onClick={() => {
              // Update document sharing settings
              setDocuments(documents.map(doc => 
                doc.document_id === sharingDoc?.document_id 
                  ? {
                      ...doc, 
                      share_with: [
                        ...(shareSettings.trustees ? ['trustees'] : []),
                        ...(shareSettings.residents ? ['residents'] : [])
                      ]
                    }
                  : doc
              ));
              toast.success('Share settings updated');
              setShowShareDialog(false);
            }}>
              <Share2 className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
